import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Monitor, Tablet, Smartphone, RotateCw, ExternalLink, AlertTriangle, Bug } from "lucide-react";
import { useOmniCraftStore } from "./store";
import { bundleForPreview } from "./esbuildPreview";
import { canBootWebContainer } from "./webcontainerHelper";
import { streamOmniCraft, parseStreamedXml } from "./stream";

const API = process.env.REACT_APP_BACKEND_URL;

const VIEWPORTS = {
  desktop: { w: "100%", h: "100%", label: "Desktop", Icon: Monitor },
  tablet: { w: "820px", h: "1180px", label: "Tablet", Icon: Tablet },
  mobile: { w: "390px", h: "780px", label: "Mobile", Icon: Smartphone },
};

export default function PreviewStage({ token }) {
  const files = useOmniCraftStore((s) => s.files);
  const nonce = useOmniCraftStore((s) => s.previewNonce);
  const viewport = useOmniCraftStore((s) => s.viewport);
  const setViewport = useOmniCraftStore((s) => s.setViewport);
  const previewError = useOmniCraftStore((s) => s.previewError);
  const setPreviewError = useOmniCraftStore((s) => s.setPreviewError);
  const pushLog = useOmniCraftStore((s) => s.pushLog);
  const setSwarmActive = useOmniCraftStore((s) => s.setSwarmActive);
  const addSubtask = useOmniCraftStore((s) => s.addSubtask);
  const updateSubtask = useOmniCraftStore((s) => s.updateSubtask);
  const applyFileMap = useOmniCraftStore((s) => s.applyFileMap);
  const modelId = useOmniCraftStore((s) => s.modelId);
  const triggerPreview = useOmniCraftStore((s) => s.triggerPreview);

  const [srcDoc, setSrcDoc] = useState("");
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBuilding(true);
      setPreviewError(null);
      try {
        const html = await bundleForPreview(files);
        if (!cancelled) setSrcDoc(html);
      } catch (e) {
        const msg = String(e?.message || e);
        if (!cancelled) {
          setPreviewError(msg);
          pushLog(`[preview] ${msg}`);
        }
      } finally {
        if (!cancelled) setBuilding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files, nonce]);

  useEffect(() => {
    function onMsg(e) {
      if (e.data?.type === "omnicraft:preview-error") {
        setPreviewError(e.data.error || "Preview crashed");
        pushLog(`[runtime] ${e.data.error}`);
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  async function autoDebug() {
    if (!previewError) return;
    setSwarmActive("debugger");
    const id = crypto.randomUUID();
    addSubtask({ id, agent: "debugger", label: "Patch compile error", tool: "patch_file()", status: "in_progress" });
    let raw = "";
    try {
      await streamOmniCraft(
        `${API}/api/admin/omnicraft/debug`,
        token,
        { model_id: modelId, error: previewError, files },
        (event, data) => {
          if (event === "token") raw += data;
          if (event === "error") {
            const err = JSON.parse(data);
            throw new Error(err.detail);
          }
        },
      );
      const parsed = parseStreamedXml(raw);
      if (!Object.keys(parsed.files).length) throw new Error("Debugger returned no files.");
      applyFileMap(parsed.files);
      updateSubtask(id, { status: "passed" });
      toast.success("Auto-patch applied");
      setPreviewError(null);
      triggerPreview();
    } catch (e) {
      updateSubtask(id, { status: "failed" });
      toast.error(e.message || "Debugger failed");
    } finally {
      setSwarmActive("orchestrator");
    }
  }

  const { Icon: ActiveIcon, w, h } = VIEWPORTS[viewport];
  const wcAvailable = canBootWebContainer();

  return (
    <div className="h-full flex flex-col bg-[#0B0D12]" data-testid="omnicraft-preview">
      <div className="border-b border-white/10 px-3 py-2 flex items-center gap-2 flex-wrap">
        <div className="flex bg-white/5 rounded-md p-0.5">
          {Object.entries(VIEWPORTS).map(([key, cfg]) => {
            const active = key === viewport;
            const I = cfg.Icon;
            return (
              <button
                key={key}
                data-testid={`omnicraft-viewport-${key}`}
                onClick={() => setViewport(key)}
                className={`px-2 py-1 rounded flex items-center gap-1 text-[11px] ${
                  active ? "bg-white/10 text-white" : "text-white/50 hover:text-white"
                }`}
              >
                <I size={11} />
                {cfg.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1" />

        <div className="text-[11px] font-mono text-white/40">
          {wcAvailable ? "webcontainer:ready" : "sandbox:esbuild"} · http://preview/
        </div>

        <button
          data-testid="omnicraft-preview-reload"
          onClick={triggerPreview}
          className="p-1.5 rounded hover:bg-white/5 text-white/50"
          title="Reload preview"
        >
          <RotateCw size={13} />
        </button>
        <button
          onClick={() => {
            const blob = new Blob([srcDoc], { type: "text/html" });
            window.open(URL.createObjectURL(blob), "_blank", "noopener");
          }}
          className="p-1.5 rounded hover:bg-white/5 text-white/50"
          title="Open preview in new tab"
        >
          <ExternalLink size={13} />
        </button>
      </div>

      <div className="flex-1 grid place-items-center bg-[#05070A] overflow-auto p-4">
        <div
          className="bg-white shadow-xl overflow-hidden rounded"
          style={{
            width: w,
            height: viewport === "desktop" ? "100%" : h,
            maxHeight: "100%",
          }}
        >
          {building && !srcDoc ? (
            <div className="w-full h-full grid place-items-center text-slate-500 text-sm">
              Compiling preview…
            </div>
          ) : (
            <div className="w-full h-full relative">
              <iframe
                key={nonce}
                title="OmniCraft Preview"
                srcDoc={srcDoc}
                sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
                className="w-full h-full border-0"
              />
              {previewError && (
                <div className="absolute inset-0 bg-rose-50/95 backdrop-blur-sm grid place-items-center p-6 text-center">
                  <div className="space-y-3 max-w-md">
                    <AlertTriangle className="mx-auto text-rose-500" size={28} />
                    <div className="text-xs font-mono text-rose-700 text-left whitespace-pre-wrap max-h-40 overflow-auto bg-white p-2 rounded">
                      {previewError}
                    </div>
                    <button
                      onClick={autoDebug}
                      data-testid="omnicraft-autodebug"
                      className="px-4 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold flex items-center gap-2 mx-auto hover:bg-slate-700"
                    >
                      <Bug size={12} /> Auto-Debug with Model
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
